import React, { Fragment } from 'react';
import { Link, Text } from '@Sage/hoi-poi-ui';
import EntityLink from 'components/EntityLink';
import { COMPANIES, CONTACTS, OPPORTUNITIES } from 'constants/Entities';
import CompanyAvatar from 'containers/components/avatars/CompanyAvatar';
import UserAvatar from 'containers/components/avatars/UserAvatar';
import ContactAvatar from 'containers/components/avatars/ContactAvatar';
import OpportunityIcon from 'containers/opportunities/components/icons/OpportunityIcon';
import {
    getCompanyDetailUrl,
    getContactFormUrl,
    getOpportunityDetailUrl,
    getUserSfmUrl,
} from 'utils/getUrl';
import { getLiteral } from 'utils/getLiteral';

// --- Company ---

const companyParseDataForPopover = (id, data) => {
    const idCompany = data?.idCompany ?? data?.value ?? id;
    const company = data?.company ?? data?.label;
    let title;
    let subtitle;
    let phone;
    let deleted;
    let multiplePhones;

    if (data?.title) {
        title = data.title;
    } else if (data?.deleted && !data?.title) {
        title = company;
    } else {
        const titleLabel = company || getLiteral('label_placeholder_unamed_record');
        title = (
            <EntityLink
                entity={COMPANIES}
                href={getCompanyDetailUrl(idCompany)}
                linkProps={{ type: 'subtitle', target: '_blank' }}
                textProps={{ type: 'subtitle' }}
            >
                {titleLabel}
            </EntityLink>
        );
    }

    if (data?.deleted) deleted = data.deleted;

    if (data?.subtitle) {
        subtitle = data.subtitle;
    } else {
        const subtitleText = data?.segment
            ? `${data.companyType}, ${data.segment}`
            : data?.companyType;
        subtitle = subtitleText ? (
            <Text type="caption">{subtitleText}</Text>
        ) : (
            <Text type="caption">-</Text>
        );
    }

    if (data?.phone1) phone = data.phone1;
    else if (data?.phone2) phone = data.phone2;
    else if (data?.mobilePhone) phone = data.mobilePhone;

    if (data?.phone1) multiplePhones = data.phone1;
    if (data?.phone2) {
        multiplePhones = multiplePhones ? `${multiplePhones}, ${data.phone2}` : data.phone2;
    }
    if (data?.mobilePhone) {
        multiplePhones = multiplePhones
            ? `${multiplePhones}, ${data.mobilePhone}`
            : data.mobilePhone;
    }

    return {
        ...data,
        title,
        subtitle,
        phone,
        deleted,
        multiplePhones,
        entityEmptyTitle: !company,
    };
};

const companyGetImage = (id) => <CompanyAvatar id={id} size="large" />;
const companyGetImageForValue = (id) => <CompanyAvatar id={id} size="small" />;

// --- User ---

const userParseDataForPopover = (id, data) => {
    const user = data?.user ?? data?.label;
    let title;
    let subtitle;

    if (data?.title) {
        title = data.title || getLiteral('label_placeholder_unamed_record');
    } else {
        title = (
            <Link type="subtitle" href={getUserSfmUrl(id)} target="_blank">
                {user || getLiteral('label_placeholder_unamed_record')}
            </Link>
        );
    }

    if (data?.subtitle) {
        subtitle = data.subtitle;
    } else {
        subtitle = data?.environment ? (
            <Text type="caption">{data.environment}</Text>
        ) : (
            <Text type="caption">-</Text>
        );
    }

    return {
        ...data,
        title,
        subtitle,
    };
};

const userGetImage = (id) => <UserAvatar id={id} size="large" />;
const userGetImageForValue = (id) => <UserAvatar id={id} size="small" />;

// --- Contact ---

const contactParseDataForPopover = (id, data) => {
    const contact = data?.contact ?? data?.label;
    const idCompany = data?.idCompany ?? data?.value ?? id;
    let title;
    let subtitle;
    let name;

    if (data?.title) {
        title = data.title || getLiteral('label_placeholder_unamed_record');
        name = title;
    } else {
        const titleLabel = contact || getLiteral('label_placeholder_unamed_record');
        title = (
            <EntityLink
                entity={CONTACTS}
                href={getContactFormUrl(id)}
                linkProps={{ type: 'subtitle', target: '_blank' }}
                textProps={{ type: 'subtitle' }}
            >
                {titleLabel}
            </EntityLink>
        );
        name = contact;
    }

    if (data?.subtitle) {
        subtitle = data.subtitle;
    } else if (data?.contactType && idCompany && data?.company) {
        subtitle = (
            <Fragment>
                <Text type="caption">{`${data.contactType}`} </Text>
                <Text type="caption">{` ${getLiteral('action_at')} `}</Text>
                <EntityLink
                    entity={COMPANIES}
                    href={getCompanyDetailUrl(idCompany)}
                    linkProps={{ type: 'caption', target: '_blank' }}
                    textProps={{ type: 'caption' }}
                >
                    {` ${data.company}`}
                </EntityLink>
            </Fragment>
        );
    } else if (data?.contactType) {
        subtitle = <Text type="caption">{data.contactType}</Text>;
    } else if (idCompany && data?.company) {
        subtitle = (
            <EntityLink
                entity={COMPANIES}
                href={getCompanyDetailUrl(idCompany)}
                linkProps={{ type: 'caption', target: '_blank' }}
                textProps={{ type: 'caption' }}
            >
                {data.company}
            </EntityLink>
        );
    } else {
        subtitle = <Text type="caption">-</Text>;
    }

    return {
        ...data,
        title,
        subtitle,
        name,
    };
};

const contactGetImage = (id) => <ContactAvatar id={id} size="large" />;
const contactGetImageForValue = (id) => <ContactAvatar id={id} size="small" />;

// --- Opportunity ---

const opportunityParseDataForPopover = (id, data) => {
    const idOpportunity = data?.idOpportunity ?? data?.value ?? id;
    const opportunity = data?.opportunity ?? data?.label;
    let title;
    let subtitle;
    const deleted = data?.deleted;

    if (data?.title) {
        title = data.title;
    } else if (deleted) {
        title = opportunity || getLiteral('label_placeholder_unamed_record');
    } else {
        const titleLabel = opportunity || getLiteral('label_placeholder_unamed_record');
        title = (
            <EntityLink
                entity={OPPORTUNITIES}
                href={getOpportunityDetailUrl(idOpportunity)}
                linkProps={{ type: 'subtitle', target: '_blank' }}
                textProps={{ type: 'subtitle' }}
            >
                {titleLabel}
            </EntityLink>
        );
    }

    if (data?.subtitle) {
        subtitle = data.subtitle;
    } else if (data?.company) {
        subtitle = (
            <div className="fm-popover__related-accounts">
                <Text type="caption">{getLiteral('label_related_accounts')}</Text>
                <EntityLink
                    entity={COMPANIES}
                    href={getCompanyDetailUrl(data.idCompany)}
                    linkProps={{ type: 'caption', target: '_blank' }}
                    textProps={{ type: 'caption' }}
                >
                    {` ${data.company} `}
                </EntityLink>
                {data.typeExpediente && (
                    <Text type="caption" className="label-popover-subtitle-line">
                        {data.typeExpediente}
                    </Text>
                )}
            </div>
        );
    } else {
        subtitle = <Text type="caption">-</Text>;
    }

    return {
        ...data,
        title,
        subtitle,
        deleted,
    };
};

const opportunityGetImage = () => <OpportunityIcon size="large" />;
const opportunityGetImageForValue = () => <OpportunityIcon />;

// --- Registry ---

const ENTITY_POPOVER_CONFIG = {
    company: {
        type: 'company',
        remotePopover: true,
        parseDataForPopover: companyParseDataForPopover,
        getImage: companyGetImage,
        getImageForValue: companyGetImageForValue,
    },
    user: {
        type: 'user',
        remotePopover: true,
        parseDataForPopover: userParseDataForPopover,
        getImage: userGetImage,
        getImageForValue: userGetImageForValue,
    },
    contact: {
        type: 'contact',
        remotePopover: true,
        parseDataForPopover: contactParseDataForPopover,
        getImage: contactGetImage,
        getImageForValue: contactGetImageForValue,
    },
    opportunity: {
        type: 'opportunity',
        remotePopover: true,
        parseDataForPopover: opportunityParseDataForPopover,
        getImage: opportunityGetImage,
        getImageForValue: opportunityGetImageForValue,
    },
};

const LIST_TO_ENTITY = {
    empresas: 'company',
    companies: 'company',
    companyrates: 'company',
    contactos: 'contact',
    contacts: 'contact',
    viewcontactosfullname: 'contact',
    usuarios: 'user',
    users: 'user',
    viewusuariosfullname: 'user',
    fullusuarios: 'user',
    expedientes: 'opportunity',
    opportunities: 'opportunity',
};

export const getLabelPopoverConfigForList = (listName) => {
    if (!listName) return null;
    const entityKey = LIST_TO_ENTITY[listName];
    if (!entityKey) return null;
    return ENTITY_POPOVER_CONFIG[entityKey] || null;
};

export default ENTITY_POPOVER_CONFIG;
